import * as THREE from 'three';
import { DrillRuntimeBase, type RuntimeSettings } from './drillRuntime';
import { GuardRecognizer, type GuardReading } from '../hit-detection/guardRecognizer';
import { guardLabel } from './guardLabels';
import { EnemyFigure } from '../scene/enemyFigure';
import { GhostSword } from '../sword/ghostSword';
import { emptyPellStats, type SessionStats } from './stats';
import {
  DRILL_TYPE,
  type DominantHand,
  type GuardMode,
  type GuardTuning,
} from '../config/constants';
import { t } from '../i18n';

export type GuardRuntimeOptions = {
  scene: THREE.Scene;
  camera: THREE.Camera;
  /** Sword visual group; the recognizer samples its world frame (honors live rotation offsets). */
  sword: THREE.Object3D;
  swordController: THREE.Object3D;
  offHandController: THREE.Object3D;
  userHeightCm: number;
  dominantHand: DominantHand;
  tuning: GuardTuning;
  /** Practice every guard ('all') or one specific guardia. */
  guardMode: GuardMode;
};

const UP = new THREE.Vector3(0, 1, 0);

/**
 * Free guard mode (issue #16, ciclo #3 osservativo): no pell, no scoring, no persistence. Shows
 * the figura del nemico at misura and — in single-guard mode — a spada fantasma at the target
 * pose that greens when recognized; in "all" mode the ghost mirrors whatever guard you hold. The
 * wrist HUD names the held guardia plus a debug line of the binned feature for in-headset tuning.
 */
export class GuardRuntime extends DrillRuntimeBase {
  override readonly drillType = DRILL_TYPE.guard;

  private readonly recognizer: GuardRecognizer;
  private readonly enemy: EnemyFigure;
  private readonly ghost: GhostSword;
  private dominantHand: DominantHand;
  /** Last string painted to the HUD — repaint only on change (cheap, avoids flicker). */
  private lastHud: string | null = null;

  // Scratch body frame — transient within `tick`, never retained.
  private readonly headPos = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly right = new THREE.Vector3();

  constructor(private opts: GuardRuntimeOptions) {
    super(opts);
    this.dominantHand = opts.dominantHand;
    this.recognizer = new GuardRecognizer({
      camera: opts.camera,
      dominantHand: opts.dominantHand,
      userHeightCm: opts.userHeightCm,
      tuning: opts.tuning,
    });
    this.enemy = new EnemyFigure(opts.userHeightCm);
    this.ghost = new GhostSword();
    opts.scene.add(this.enemy.group);
    opts.scene.add(this.ghost.group);
  }

  start(): void {
    this.recognizer.reset();
    this.lastHud = null;
    this.enemy.place(this.opts.camera);
    this.ghost.hide();
  }

  /** Observational mode: nothing to aggregate or persist. */
  end(): SessionStats {
    return emptyPellStats();
  }

  tick(): void {
    const dt = this.clock.getDelta();
    const reading = this.recognizer.recognize(
      this.opts.sword,
      dt,
      this.enemy.eyes,
      this.enemy.belt,
    );
    this.enemy.update(this.opts.camera);
    this.enemy.setAim(reading.aimPoint);
    this.updateGhost(reading);
    this.renderHud(reading);
  }

  private updateGhost(reading: GuardReading): void {
    const target = this.opts.guardMode;
    if (target !== 'all') {
      // Single-guard mode: always show the target pose, green when the held guard matches it.
      if (this.bodyFrame()) {
        this.ghost.show(
          target,
          this.headPos,
          this.forward,
          this.right,
          this.dominantHand,
          this.opts.userHeightCm / 100,
        );
      }
      this.ghost.setMatched(reading.settled && reading.guard === target);
      return;
    }
    // "All" mode: the ghost mirrors the guard you are recognized as; hidden when fuori guardia.
    if (reading.settled && reading.guard && this.bodyFrame()) {
      this.ghost.show(
        reading.guard,
        this.headPos,
        this.forward,
        this.right,
        this.dominantHand,
        this.opts.userHeightCm / 100,
      );
      this.ghost.setMatched(true);
    } else {
      this.ghost.hide();
    }
  }

  /**
   * Body frame for the ghost: forward = the **directrix** toward the enemy (not the gaze), right =
   * the user's right. The ghost prototypes are authored in this frame, so the ghost matches what
   * the recognizer measures. False when the user is on top of the enemy (degenerate directrix).
   */
  private bodyFrame(): boolean {
    this.opts.camera.getWorldPosition(this.headPos);
    this.forward.copy(this.enemy.eyes).sub(this.headPos);
    this.forward.y = 0;
    if (this.forward.lengthSq() < 1e-9) return false;
    this.forward.normalize();
    this.right.crossVectors(this.forward, UP);
    return true;
  }

  private renderHud(reading: GuardReading): void {
    const recognized = reading.settled && reading.guard != null;
    const text = recognized ? guardLabel(reading.guard!) : t('guardNone');
    const debug = debugText(reading);
    const key = `${text}|${debug}`;
    if (key === this.lastHud) return;
    this.lastHud = key;
    this.hud.render({ timerLabel: '', lastShot: null, guard: { text, recognized, debug } });
  }

  setUserHeightCm(cm: number): void {
    this.opts.userHeightCm = cm;
    this.recognizer.setUserHeightCm(cm);
  }

  applySettings(settings: Partial<RuntimeSettings>): void {
    if (settings.dominantHand !== undefined) {
      this.dominantHand = settings.dominantHand;
      this.recognizer.setDominantHand(settings.dominantHand);
    }
    if (settings.guardTuning) this.recognizer.setTuning(settings.guardTuning);
  }

  protected onControllersChanged(): void {
    this.recognizer.reset();
    this.lastHud = null;
  }

  override dispose(): void {
    super.dispose();
    this.enemy.dispose();
    this.ghost.dispose();
  }
}

/** The binned feature as a compact debug string: mano·aim-altezza·aim-lato·mano-roll, or status. */
function debugText(reading: GuardReading): string {
  if (!reading.settled) return '· · ·';
  const f = reading.feature;
  if (!f) return '—';
  return `${f.handHeight}·${f.aimHeight}·${f.aimSide ?? '—'}·${f.handRoll ?? '—'}`;
}
